import { clone } from 'lodash'
import { boundsToQueryString } from '@/services/geo/bounds'

// Convert a GeoJSON Polygon ring ([[lng, lat], ...]) into the Google-Maps
// style path the SearchAreaPolygon component renders ([{lat, lng}, ...]).
function ringToPaths(ring) {
  if (!Array.isArray(ring)) return null
  const paths = []
  for (let i = 0; i < ring.length; i += 1) {
    const point = ring[i]
    if (!Array.isArray(point) || point.length < 2) return null
    const lng = Number(point[0])
    const lat = Number(point[1])
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null
    paths.push({ lat, lng })
  }
  return paths.length ? paths : null
}

function polygonToPaths(polygon) {
  const ring = polygon?.coordinates?.[0]
  return ringToPaths(ring)
}

export default {
  namespaced: true,

  state: {
    markers: [],
    mapBounds: null,
    searchBounds: null,
    geoBoundingBox: null,
    searchAreaBasePaths: null,
    searchAreaCircle: null,
    searchAreaCentroid: null,
    searchAreaSource: null,
    searchAreaPlaceId: null,
    routeSetup: true,
    loadingMap: false,
  },

  mutations: {
    addMarker(state, marker) {
      state.markers.push(marker)
    },
    setMarkers(state, markers) {
      state.markers = markers
    },
    addGeoBoundingBox(state, geoBoundingBox) {
      state.geoBoundingBox = clone(geoBoundingBox)
    },
    addMapBounds(state, mapBounds) {
      state.mapBounds = clone(mapBounds)
    },
    addSearchBounds(state, searchBounds) {
      state.searchBounds = clone(searchBounds)
    },
    updateLoadingMap(state, isLoading) {
      state.loadingMap = clone(isLoading)
    },
    toggleRouteSetup(state, setup) {
      state.routeSetup = setup
    },
    clearGeoBoundingBox(state) {
      state.geoBoundingBox = null
    },
    clearSearchBounds(state) {
      state.searchBounds = null
    },
    setSearchAreaBasePaths(state, paths) {
      state.searchAreaBasePaths = paths ? clone(paths) : null
    },
    setSearchAreaCircle(state, circle) {
      state.searchAreaCircle = circle ? clone(circle) : null
    },
    setSearchAreaCentroid(state, centroid) {
      state.searchAreaCentroid = centroid ? clone(centroid) : null
    },
    setSearchAreaSource(state, source) {
      state.searchAreaSource = source ?? null
    },
    setSearchAreaPlaceId(state, placeId) {
      state.searchAreaPlaceId = placeId ?? null
    },
    clearState(state) {
      state.markers = []
      state.mapBounds = null
      state.searchBounds = null
      state.geoBoundingBox = null
      state.searchAreaBasePaths = null
      state.searchAreaCircle = null
      state.searchAreaCentroid = null
      state.searchAreaSource = null
      state.searchAreaPlaceId = null
      state.loadingMap = false
    },
  },

  actions: {
    clearState: ({ commit }) => new Promise((resolve) => {
      commit('clearState')
      resolve()
    }),

    updateLoadingMap: ({ commit }, isLoading) => new Promise((resolve, reject) => {
      commit('updateLoadingMap', isLoading)
      resolve()
    }),

    toggleRouteSetup: ({ commit }, setup) => new Promise((resolve) => {
      commit('toggleRouteSetup', setup)
      resolve()
    }),

    setMarkers: async ({ commit, dispatch }, markers) => {
      await commit('setMarkers', markers)
      await dispatch('updateLoadingMap', false)
    },
    addMapBounds: ({ commit }, bounds) => new Promise((resolve) => {
      commit('addMapBounds', bounds)
      resolve()
    }),

    addAndFilterBounds: ({
      commit,
      dispatch,
      state,
      rootGetters,
    }, bounds) => new Promise((resolve) => {
      if (state.routeSetup) {
        commit('toggleRouteSetup', false)
        commit('addMapBounds', bounds)
        const searchTerm = rootGetters['garden/searchTerm']
        const searchTypeValue = rootGetters['garden/searchTypeValue']
        if (searchTerm && searchTypeValue === 'location') {
          // commit('clearSearchBounds')
          // dispatch('garden/clearSearch', [{}], { root: true })
        }

        dispatch('addGeoBoundingBox', bounds)

        dispatch('filter/addBoundsFilter', bounds, { root: true })
      }
      resolve()
    }),

    addSearchBounds: ({ commit, dispatch }, bounds) => new Promise((resolve) => {
      commit('addSearchBounds', bounds)

      dispatch('addGeoBoundingBox', bounds)
      resolve()
    }),

    /**
     * Re-search the live map viewport after a genuine user pan/zoom on a
     * NON-county search (D5). The viewport becomes the search: the location
     * label legitimately drops (clearSearch nulls the selected location), and
     * - critically - we update searchBounds to the live viewport BEFORE
     * refetching so the API request, the URL, and the visible viewport all
     * agree. Without this, filterGardens kept building the API
     * filter[geo_bounding_box] from the stale original searchBounds while the
     * URL was rewritten to the live viewport, leaving the two out of sync on
     * every zoom.
     *
     * @param {object} bounds TRBRBLTL bounds of the live viewport.
     */
    searchLiveViewport: async ({ commit, dispatch }, bounds) => {
      await dispatch('garden/clearSearch', '', { root: true })
      await dispatch('garden/updateMapSearch', true, { root: true })
      commit('addSearchBounds', bounds)
      await dispatch('addAndFilterBounds', bounds)
    },

    /**
     * Option B drag-override (D2), pan-only. Fired ONLY on a genuine user
     * drag of the map after a county search (the GoogleMapLoader `dragend`
     * handler is the sole caller; programmatic fitBounds/setZoom never emit
     * `dragend`, so a county search drawing itself can never trigger this,
     * and a scroll-zoom does not either - zoom during a county search keeps
     * the lock, that handover is out of scope).
     *
     * This ONLY releases the county search_area (clears the overlay + lock,
     * flipping isSearchAreaLocked false). It deliberately does NOT run the
     * viewport search itself: on a real drag Google fires `dragend` then
     * `idle`, and once the lock is released the existing idle -> localBounds
     * unlocked path runs the viewport search exactly once (clearSearch nulls
     * the selected location so the county params stop, searchLiveViewport
     * updates searchBounds + refetches). Doing the search here too would
     * double-fetch.
     */
    releaseSearchAreaForDrag: ({ dispatch }) => dispatch('setSearchArea', null),

    clearSearchBounds: ({ commit }) => new Promise((resolve) => {
      commit('clearSearchBounds')
      resolve()
    }),

    setSearchAreaBasePaths: ({ commit }, paths) => new Promise((resolve) => {
      commit('setSearchAreaBasePaths', paths)
      resolve()
    }),

    setSearchAreaPlaceId: ({ commit }, placeId) => new Promise((resolve) => {
      commit('setSearchAreaPlaceId', placeId)
      resolve()
    }),

    /**
     * Consumes the `search_area` block from the /api/gardens response.
     * Pass null to clear (e.g. when a non-county search runs).
     */
    setSearchArea: ({ commit }, searchArea) => new Promise((resolve) => {
      if (!searchArea || typeof searchArea !== 'object') {
        commit('setSearchAreaBasePaths', null)
        commit('setSearchAreaCircle', null)
        commit('setSearchAreaCentroid', null)
        commit('setSearchAreaSource', null)
        commit('setSearchAreaPlaceId', null)
        resolve()
        return
      }

      const basePaths = polygonToPaths(searchArea.polygon)
      commit('setSearchAreaBasePaths', basePaths)

      if (searchArea.mode === 'circle' && searchArea.circle) {
        commit('setSearchAreaCircle', {
          center: {
            lat: Number(searchArea.circle.center?.lat),
            lng: Number(searchArea.circle.center?.lng),
          },
          radiusMeters: Number(searchArea.circle.radius_meters),
        })
      } else {
        // Polygon-mode: the expansion ring is computed locally by
        // SearchAreaPolygon.vue via @turf/buffer, not stored here.
        commit('setSearchAreaCircle', null)
      }

      const centroid = searchArea.centroid
        ? { lat: Number(searchArea.centroid.lat), lng: Number(searchArea.centroid.lng) }
        : null
      commit('setSearchAreaCentroid', centroid)
      commit('setSearchAreaSource', searchArea.source ?? null)
      commit('setSearchAreaPlaceId', searchArea.place_id ?? null)
      resolve()
    }),

    addGeoBoundingBox: ({ commit }, geoBoundingBox) => new Promise((resolve) => {
      const boundingBox = {
        top_right: {
          lat: Number(geoBoundingBox?.top_right?.lat),
          lng: Number(geoBoundingBox?.top_right?.lng),
        },
        bottom_right: {
          lat: Number(geoBoundingBox?.bottom_right?.lat),
          lng: Number(geoBoundingBox?.bottom_right?.lng),
        },
        bottom_left: {
          lat: Number(geoBoundingBox?.bottom_left?.lat),
          lng: Number(geoBoundingBox?.bottom_left?.lng),
        },
        top_left: {
          lat: Number(geoBoundingBox?.top_left?.lat),
          lng: Number(geoBoundingBox?.top_left?.lng),
        },
      };

      commit('addGeoBoundingBox', boundingBox)
      resolve()
    }),
  },

  getters: {
    markers: (state) => state.markers,
    mapBounds: (state) => state.mapBounds,
    searchBounds: (state) => state.searchBounds,
    searchBoundsQuery: (state) => boundsToQueryString(state.searchBounds),
    geoBoundingBox: (state) => state.geoBoundingBox,
    searchAreaBasePaths: (state) => state.searchAreaBasePaths,
    searchAreaCircle: (state) => state.searchAreaCircle,
    searchAreaCentroid: (state) => state.searchAreaCentroid,
    searchAreaSource: (state) => state.searchAreaSource,
    // 'polygon' | 'circle' | null. Use when components need to discriminate
    // between modes (e.g. to render different overlays). For the common
    // "is anything locked" check, prefer `isSearchAreaLocked` below for
    // intent.
    searchAreaFilter: (state) => {
      if (state.searchAreaCircle) return 'circle'
      if (state.searchAreaBasePaths) return 'polygon'
      return null
    },
    // True while the API has resolved a county search_area (polygon OR
    // circle mode). Drives the map's drag/zoom-refetch suppression and
    // marker/location pin gates so pan and radius changes don't redraw
    // or refetch on top of an active overlay.
    isSearchAreaLocked: (state, getters) => getters.searchAreaFilter !== null,
    searchAreaPlaceId: (state) => state.searchAreaPlaceId,
    routeSetup: (state) => state.routeSetup,
    loadingMap: (state) => state.loadingMap,
  },
}
